import { useCallback, useMemo } from "react"

import { useAgentNamesTransport } from "@/agents/transport"
import {
  buildMetricIdentityThresholdSourcesById,
  useMetricsList,
  type MetricIdentityThresholdSource,
  type MetricThresholdSource,
} from "@/metrics/catalog"

import type { QueryDashboardBatchRequest } from "@/types_api/backend/transport"
import type { TargetCondition } from "@/types/metrics/contracts"
import {
  calculateDynamicDateRange,
  calculatePresetDateRange,
} from "@/utils/date/dateRange"

import type { MetricWidgetConfig } from "../components/BaseWidget"
import { useDashboardFilters } from "../contexts/DashboardFiltersContext"
import { useDashboardBatchTransportQuery } from "../routes/dashboardBatchTransportQuery"
import { GROUP_BY_TYPE, type GroupByType } from "../types/chart-types"
import {
  AGGREGATION_TYPE,
  type FilterType,
  type MetricFilter,
} from "../types/core"
import { DEFAULT_WIDGET_DATE_RANGE } from "../types/dateRange"
import type { MetricValue } from "../utils/chartShared"
import {
  isHumanReviewSource,
  isUploadedConversationSource,
  resolveDataSource,
} from "../utils/dataSource"
import { reconcileTableMetadataCohorts } from "../utils/tableMetadataCohorts"
import {
  partitionMetricIdsByOutputType,
  resolveTableMetricIds,
} from "../utils/tableWidgetData"
import { normalizeTableMetricValues } from "../utils/tableWidgetTransportData"
import { getBrowserTimezone } from "../utils/timeBucketing"
import {
  isTransportDashboardQueryLoading,
  serializeDashboardBatchQuery,
} from "../utils/transportDashboardQueries"
import {
  getWidgetMetricFilterError,
  getWidgetMetricFilters,
} from "../utils/widgetMetricFilterValidation"
import {
  isWidgetMetricOutputType,
  withWidgetMetricOutputTypes,
} from "../utils/widgetMetricOutputType"
import { getWidgetMetricReferenceIds } from "../utils/widgetMetricReferences"

interface UseTableWidgetDataParams {
  config: MetricWidgetConfig & {
    type: "table"
    groupBy?: GroupByType
    groupByMetadataKey?: string
    filters?: FilterType
    metricFilter?: MetricFilter[]
  }
}

interface TableWidgetDataResult {
  dateRange: { end: Date; start: Date }
  error: string | null
  metricRefreshError: string | null
  hasMetrics: boolean
  isGlobalOverrideActive: boolean
  isLoading: boolean
  metricRecords: Record<string, MetricIdentityThresholdSource>
  metricValues: MetricValue[]
  retry: () => void
}

const CHART_QUERY_GC_TIME = 15 * 60 * 1000
const CHART_QUERY_STALE_TIME = 5 * 60 * 1000

const buildTargetConditionsByMetricId = ({
  metricIds,
  metricRecords,
}: {
  metricIds: string[]
  metricRecords: Record<string, MetricThresholdSource>
}) => {
  if (metricIds.length === 0) {
    return undefined
  }

  const targetConditionsByMetricId = metricIds.reduce<
    Record<string, TargetCondition>
  >((acc, metricId) => {
    const targetCondition =
      metricRecords[metricId]?.metric_metadata?.target_condition
    if (targetCondition) {
      acc[metricId] = targetCondition
    }
    return acc
  }, {})

  return Object.keys(targetConditionsByMetricId).length > 0
    ? targetConditionsByMetricId
    : undefined
}

export const useTableWidgetData = ({
  config: savedConfig,
}: UseTableWidgetDataParams): TableWidgetDataResult => {
  const config = useMemo(
    () => ({
      ...savedConfig,
      metricFilter: getWidgetMetricFilters(savedConfig.metricFilter),
    }),
    [savedConfig],
  )
  const { state } = useDashboardFilters()
  const {
    dateRange: globalDateRange,
    globalDateRangeOverride,
    isOverrideActive,
  } = state

  const effectiveDateRange = useMemo(() => {
    if (isOverrideActive && globalDateRangeOverride) {
      return globalDateRangeOverride
    }
    if (globalDateRange) {
      return globalDateRange
    }

    if (config.dateRange) {
      if (config.dateRange.preset) {
        return calculatePresetDateRange(config.dateRange.preset)
      }

      if (config.dateRange.relativeConfig) {
        return calculateDynamicDateRange(
          config.dateRange.relativeConfig.amount,
          config.dateRange.relativeConfig.unit,
        )
      }

      if (config.dateRange.customRange) {
        return config.dateRange.customRange
      }
    }

    return calculatePresetDateRange(DEFAULT_WIDGET_DATE_RANGE.preset!)
  }, [
    config.dateRange,
    globalDateRange,
    globalDateRangeOverride,
    isOverrideActive,
  ])

  const effectiveMetricIds = useMemo(() => {
    return resolveTableMetricIds({
      filters: config.filters,
      metricId: config.metricId,
      metricIds: config.metricIds,
    })
  }, [config.filters, config.metricId, config.metricIds])

  const hasMetrics = effectiveMetricIds.length > 0
  const referencedMetricIds = useMemo(
    () => [
      ...new Set([
        ...effectiveMetricIds,
        ...getWidgetMetricReferenceIds(config),
      ]),
    ],
    [effectiveMetricIds, config],
  )

  const {
    data: allOrgMetricRecords,
    isLoading: isMetricRecordsLoading,
    error: metricRecordsError,
    metricIdAliases,
    metricDirectoryByManager: byManager,
    refetch: refetchMetrics,
  } = useMetricsList({
    enabled: hasMetrics,
    referencedMetricIds,
    revalidateOnMount: "stale",
  })

  const definitions = useMemo(
    () =>
      buildMetricIdentityThresholdSourcesById(
        withWidgetMetricOutputTypes(allOrgMetricRecords, byManager),
        metricIdAliases,
      ),
    [allOrgMetricRecords, metricIdAliases, byManager],
  )
  const metricRecords = useMemo(
    () =>
      Object.fromEntries(
        effectiveMetricIds.flatMap((id) =>
          definitions[id] ? [[id, definitions[id]]] : [],
        ),
      ),
    [definitions, effectiveMetricIds],
  )
  const metricFilterError = getWidgetMetricFilterError(
    config.metricFilter,
    definitions,
  )

  const hasUnresolvedMetrics = effectiveMetricIds.some(
    (id) => !metricRecords[id],
  )
  const unsupportedMetrics = effectiveMetricIds.some(
    (id) =>
      metricRecords[id] &&
      !isWidgetMetricOutputType(metricRecords[id].output_type),
  )
  const definitionsReady =
    !isMetricRecordsLoading &&
    !hasUnresolvedMetrics &&
    !metricFilterError &&
    !unsupportedMetrics
  const partitions = useMemo(
    () =>
      definitionsReady
        ? partitionMetricIdsByOutputType({
            metricIds: effectiveMetricIds,
            metricRecords,
          })
        : [],
    [effectiveMetricIds, metricRecords, definitionsReady],
  )

  const floatMetricIds = useMemo(
    () =>
      partitions.find((partition) => partition.outputType === "float")
        ?.metricIds ?? [],
    [partitions],
  )
  const stringMetricIds = useMemo(
    () =>
      partitions.find((partition) => partition.outputType === "string")
        ?.metricIds ?? [],
    [partitions],
  )

  const dataSource = resolveDataSource(config)
  const isMonitoring = isUploadedConversationSource(dataSource)
  const isHumanReview = isHumanReviewSource(dataSource)
  const timezone = getBrowserTimezone()
  const hasFloatMetrics = hasMetrics && floatMetricIds.length > 0
  const hasStringMetrics = hasMetrics && stringMetricIds.length > 0
  const isFloatDashboardQueryEnabled = hasFloatMetrics && !isHumanReview
  const isStringDashboardQueryEnabled = hasStringMetrics && !isHumanReview
  const isFloatHumanReviewQueryEnabled = hasFloatMetrics && isHumanReview
  const isStringHumanReviewQueryEnabled = hasStringMetrics && isHumanReview

  const dashboardBatchRequest = useMemo(() => {
    const queries: QueryDashboardBatchRequest["queries"] = []

    if (isFloatDashboardQueryEnabled) {
      queries.push(
        serializeDashboardBatchQuery({
          query_id: "float_dashboard",
          kind: "queryDashboardMetric",
          type: "table",
          metricOutputType: "float",
          aggregation: config.aggregation || AGGREGATION_TYPE.SUCCESS,
          monitoring: isMonitoring,
          startDate: effectiveDateRange.start.toISOString(),
          endDate: effectiveDateRange.end.toISOString(),
          filters: {
            ...config.filters,
            metricIds: floatMetricIds,
          },
          groupBy:
            config.groupBy === GROUP_BY_TYPE.ASSIGNEE
              ? undefined
              : config.groupBy,
          groupByMetadataKey: config.groupByMetadataKey,
          timezone,
          metricFilter: config.metricFilter,
          targetConditionsByMetricId: buildTargetConditionsByMetricId({
            metricIds: floatMetricIds,
            metricRecords,
          }),
        }),
      )
    }

    if (isStringDashboardQueryEnabled) {
      queries.push(
        serializeDashboardBatchQuery({
          query_id: "string_dashboard",
          kind: "queryDashboardMetric",
          type: "table",
          metricOutputType: "string",
          aggregation: config.aggregation || AGGREGATION_TYPE.SUCCESS,
          monitoring: isMonitoring,
          startDate: effectiveDateRange.start.toISOString(),
          endDate: effectiveDateRange.end.toISOString(),
          filters: {
            ...config.filters,
            metricIds: stringMetricIds,
          },
          groupBy:
            config.groupBy === GROUP_BY_TYPE.ASSIGNEE
              ? undefined
              : config.groupBy,
          groupByMetadataKey: config.groupByMetadataKey,
          timezone,
          metricFilter: config.metricFilter,
          targetConditionsByMetricId: buildTargetConditionsByMetricId({
            metricIds: stringMetricIds,
            metricRecords,
          }),
        }),
      )
    }

    if (isFloatHumanReviewQueryEnabled) {
      queries.push(
        serializeDashboardBatchQuery({
          query_id: "float_human_review",
          kind: "queryHumanReviewMetric",
          type: "table",
          metricIds: floatMetricIds,
          metricOutputType: "float",
          aggregation: config.aggregation || AGGREGATION_TYPE.SUCCESS,
          startDate: effectiveDateRange.start.toISOString(),
          endDate: effectiveDateRange.end.toISOString(),
          timezone,
          groupBy:
            config.groupBy === GROUP_BY_TYPE.ASSIGNEE
              ? GROUP_BY_TYPE.ASSIGNEE
              : undefined,
          filters: config.filters,
          metricFilter: config.metricFilter,
        }),
      )
    }

    if (isStringHumanReviewQueryEnabled) {
      queries.push(
        serializeDashboardBatchQuery({
          query_id: "string_human_review",
          kind: "queryHumanReviewMetric",
          type: "table",
          metricIds: stringMetricIds,
          metricOutputType: "string",
          aggregation: config.aggregation || AGGREGATION_TYPE.SUCCESS,
          startDate: effectiveDateRange.start.toISOString(),
          endDate: effectiveDateRange.end.toISOString(),
          timezone,
          groupBy:
            config.groupBy === GROUP_BY_TYPE.ASSIGNEE
              ? GROUP_BY_TYPE.ASSIGNEE
              : undefined,
          filters: config.filters,
          metricFilter: config.metricFilter,
        }),
      )
    }

    return { queries }
  }, [
    config.aggregation,
    config.filters,
    config.groupBy,
    config.groupByMetadataKey,
    config.metricFilter,
    effectiveDateRange.end,
    effectiveDateRange.start,
    floatMetricIds,
    isFloatDashboardQueryEnabled,
    isFloatHumanReviewQueryEnabled,
    isMonitoring,
    isStringDashboardQueryEnabled,
    isStringHumanReviewQueryEnabled,
    metricRecords,
    stringMetricIds,
    timezone,
  ])

  const dashboardBatchQueryEnabled = dashboardBatchRequest.queries.length > 0
  const dashboardBatchQuery = useDashboardBatchTransportQuery({
    request: dashboardBatchRequest,
    options: {
      enabled: dashboardBatchQueryEnabled,
      gcTime: CHART_QUERY_GC_TIME,
      staleTime: CHART_QUERY_STALE_TIME,
    },
  })

  const combinedRows = useMemo(() => {
    const results = dashboardBatchQuery.data?.results
    if (!results) {
      return []
    }

    return config.groupByMetadataKey && results.length > 1
      ? reconcileTableMetadataCohorts(results)
      : results.flatMap((result) => result.rows)
  }, [config.groupByMetadataKey, dashboardBatchQuery.data?.results])

  const uniqueAgentIds = useMemo(() => {
    const agentIds = combinedRows
      .map((row) =>
        typeof row.run_agent_id === "string" ? row.run_agent_id : null,
      )
      .filter((agentId): agentId is string => agentId != null)

    return Array.from(new Set(agentIds))
  }, [combinedRows])

  const { data: agentNames, isLoading: isAgentNamesLoading } =
    useAgentNamesTransport(uniqueAgentIds)

  const metricValues = useMemo(
    () =>
      normalizeTableMetricValues({
        agentNames,
        groupByMetadataKey: config.groupByMetadataKey,
        rows: combinedRows,
      }),
    [agentNames, combinedRows, config.groupByMetadataKey],
  )

  const isLoading =
    (hasMetrics && isMetricRecordsLoading) ||
    isTransportDashboardQueryLoading(
      dashboardBatchQuery,
      dashboardBatchQueryEnabled,
    ) ||
    isAgentNamesLoading

  const metricErrorMessage =
    metricRecordsError instanceof Error
      ? metricRecordsError.message
      : metricRecordsError
        ? "Metric definitions could not be loaded."
        : null
  const error = hasUnresolvedMetrics
    ? (metricErrorMessage ??
      (!isMetricRecordsLoading
        ? "Some selected metric definitions could not be resolved. Retry loading metrics or edit the widget to remove those selections."
        : null))
    : (!isMetricRecordsLoading && unsupportedMetrics
        ? "Some selected metrics have unsupported output types. Edit the widget to choose supported metrics."
        : null) ||
      (!isMetricRecordsLoading && metricFilterError) ||
      dashboardBatchQuery.error?.message ||
      null
  const { refetch: refetchDashboard } = dashboardBatchQuery
  const retry = useCallback(() => {
    void refetchMetrics()
    if (dashboardBatchQueryEnabled) {
      void refetchDashboard()
    }
  }, [refetchMetrics, refetchDashboard, dashboardBatchQueryEnabled])

  return {
    dateRange: effectiveDateRange,
    error,
    metricRefreshError: !hasUnresolvedMetrics ? metricErrorMessage : null,
    hasMetrics,
    isGlobalOverrideActive: isOverrideActive,
    isLoading,
    metricRecords,
    metricValues,
    retry,
  }
}
